import React from 'react'
import { Card, Select } from 'antd'
import { WarningOutlined, PushpinFilled } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

import { getFormattedDaysAndHours, getRawDaysOrHours } from '@vacationtracker/shared/functions/get-days-or-hours'
import { getProbationPeriodInfo } from '../utils/dashboardCalculations'
import IntlMessages from '../../../util/IntlMessages'
import { userHasWorkingHoursSchedule } from '@vacationtracker/shared/functions/work-week'

import { IQuotaAmount, ShortestLeaveIntervalEnum } from '@vacationtracker/shared/types/leave-policy'
import { LeaveDisplayFormatEnum } from '@vacationtracker/shared/types/display-in-days-hours'
import { IUserLeaveTypeResult, IGetUser } from '../../../types/custom-queries'
import { IRolloverExpiry } from '..'

const { Option } = Select

interface BalanceCardProps {
  currentYearLeaveTypes: IUserLeaveTypeResult[]
  currentYearStart: string
  currentYearEnd: string
  selectedLeaveType: IUserLeaveTypeResult | null
  onLeaveTypeChange: (leaveTypeId: string) => void
  user?: IGetUser | null
  isLoading?: boolean
  className?: string
  hourlyAccounting?: boolean
  rolloverExpiry?: IRolloverExpiry | null
}

const BalanceCardComponent: React.FC<BalanceCardProps> = ({
  currentYearLeaveTypes,
  currentYearStart,
  currentYearEnd,
  selectedLeaveType,
  onLeaveTypeChange,
  user,
  isLoading = false,
  className = '',
  hourlyAccounting = false,
  rolloverExpiry,
}) => {
  const { formatMessage } = useIntl()
  const userHasSchedule = user?.workHours && userHasWorkingHoursSchedule(user.workHours.schedule || user.workHours.workHours)

  // Handle case when user has no leave types
  if (!selectedLeaveType || currentYearLeaveTypes.length === 0) {
    return (
      <Card className={`balance-card balance-card-no-leave-types ${className}`}>
        <div>
          <div className="balance-header">
            <div className="balance-info">
              <div className="balance-amount">
                <WarningOutlined style={{ fontSize: 32, marginRight: 8 }} />
                {formatMessage({ id: 'dashboard.balance.noLeaveTypes' })}
              </div>
              <div className="balance-label">{formatMessage({ id: 'dashboard.balance.noLeaveTypesDescription' })}</div>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // Filter active leave types for dropdown
  const activeLeaveTypes = currentYearLeaveTypes.filter((leaveType) => leaveType.isActive).sort((a, b) => a.position - b.position)
  // Determine default (primary) leave type by smallest position value (first element after sorting)
  const defaultLeaveTypeId = activeLeaveTypes.length > 0 ? activeLeaveTypes[0].leaveTypeId : undefined

  const { hasUnlimitedDays, quotas, shortestLeaveInterval } = selectedLeaveType

  // Helper function to determine if we should show minutes for this leave type
  const shouldShowMinutes = (interval?: ShortestLeaveIntervalEnum) => {
    return interval === ShortestLeaveIntervalEnum.halfHour || interval === ShortestLeaveIntervalEnum.fifteenMinutes || interval === ShortestLeaveIntervalEnum.oneHour
  }

  // Helper function to format balance display using shared formatting logic
  const formatBalanceDisplay = (
    quota: IQuotaAmount,
    hasUnlimitedDays: boolean,
    displayFormat: LeaveDisplayFormatEnum = LeaveDisplayFormatEnum.long,
    hasSchedule = false
  ): string => {
    if (hasUnlimitedDays) {
      return formatMessage({ id: 'app.unlimited' })
    }

    const showMinutes = shouldShowMinutes(shortestLeaveInterval)
    const computedValue = {
      days: Math.abs(quota.days),
      hours: Math.abs(quota.hours),
      minutes: showMinutes ? Math.abs(quota.minutes) : 0, // Use actual minutes from API
      isNegative: quota.isNegative,
    }

    if (computedValue.days === 0 && computedValue.hours === 0 && computedValue.minutes === 0) {
      return '0'
    }

    if (hasSchedule) {
      return getRawDaysOrHours(
        {
          raw: {
            days: quota.raw.days,
            hours: quota.raw.hours,
          },
          ...computedValue,
        },
        'hours',
        formatMessage,
        LeaveDisplayFormatEnum.short
      )
    }

    return getFormattedDaysAndHours({
      leave: computedValue,
      formatMessage,
      displayFormat,
    })
  }

  // Calculate available days
  const isNegativeBalance = quotas.remaining.isNegative

  // Check if user is in probation period for this leave type
  const probationInfo =
    user && user.startDate && user.probationPeriod ? getProbationPeriodInfo(user.startDate, user.probationPeriod, selectedLeaveType.leaveTypeId) : { inProbation: false }

  // Determine card state for styling
  const getCardState = () => {
    if (probationInfo.inProbation) return 'probation'
    if (hasUnlimitedDays) return 'unlimited'
    if (isNegativeBalance) return 'negative'
    return 'default'
  }

  return (
    <Card className={`balance-card balance-card-${getCardState()} ${className}`}>
      {/* Header with balance and selector */}
      <div className="balance-header">
        <div className={`balance-info ${activeLeaveTypes.length > 1 ? 'with-selector' : ''}`}>
          <div className="balance-amount">
            {probationInfo.inProbation ? (
              <IntlMessages id="app.probationPeriod" />
            ) : (
              formatBalanceDisplay(quotas.remaining, hasUnlimitedDays, LeaveDisplayFormatEnum.short, userHasSchedule)
            )}
          </div>
          <div className="balance-label">
            {probationInfo.inProbation
              ? `${formatMessage({ id: 'app.probationPeriodUntil' })}: ${dayjs(probationInfo.endDate).format('MMM DD, YYYY')}`
              : formatMessage({ id: 'app.leaveTypeBalance' }, { leaveTypeName: selectedLeaveType?.leaveTypeName })}
          </div>
        </div>

        {/* Leave type selector - only show if there are multiple active leave types */}
        {activeLeaveTypes.length > 1 && (
          <div className="leave-type-selector">
            <Select
              value={selectedLeaveType.leaveTypeId}
              onChange={onLeaveTypeChange}
              style={{ minWidth: 130 }}
              size="small"
              loading={isLoading}
              dropdownStyle={{ borderRadius: 8 }}
            >
              {activeLeaveTypes.map((leaveType) => (
                <Option key={leaveType.leaveTypeId} value={leaveType.leaveTypeId}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{leaveType.leaveTypeName}</span>
                    {leaveType.leaveTypeId === defaultLeaveTypeId && <PushpinFilled style={{ color: '#7f00ff' }} />}
                  </span>
                </Option>
              ))}
            </Select>
          </div>
        )}
      </div>

      {/* Fiscal Year Information */}
      <div className="fiscal-year-info">
        <div className="fiscal-year-dates">
          {currentYearStart && currentYearEnd
            ? `${dayjs(currentYearStart).utc().format('MMMM D, YYYY')} - ${dayjs(currentYearEnd).utc().format('MMMM D, YYYY')}`
            : formatMessage({ id: 'app.currentYear' })}
        </div>
      </div>

      {/* Balance breakdown - Always show for transparency */}
      <div className="quota-breakdown-container">
        <div className="quota-breakdown">
          <div className="quota-item">
            <div className="quota-value">
              {
                // We always show the used balance, even if the leave type is unlimited
                formatBalanceDisplay(quotas.used, false, LeaveDisplayFormatEnum.short, userHasSchedule)
              }
            </div>
            <div className="quota-label">
              <IntlMessages id="app.taken" />
            </div>
          </div>
          <div className="quota-item">
            <div className="quota-value">
              {
                // We always show the scheduled balance, even if the leave type is unlimited
                formatBalanceDisplay(quotas.scheduled, false, LeaveDisplayFormatEnum.short, userHasSchedule)
              }
            </div>
            <div className="quota-label">
              <IntlMessages id="app.scheduled" />
            </div>
          </div>
          <div className="quota-item">
            <div className="quota-value">
              {(() => {
                return formatBalanceDisplay(quotas.pending, false, LeaveDisplayFormatEnum.short, userHasSchedule)
              })()}
            </div>
            <div className="quota-label">
              <IntlMessages id="app.requested" />
            </div>
          </div>
        </div>
      </div>

      {
        /* Cap hit warning */
        selectedLeaveType?.capHit ? (
          <div className="balance-card-warning">⚠️ {formatMessage({ id: 'components.leavePolicyForm.accrualCapWarning' }, { leaveTypeName: selectedLeaveType.leaveTypeName })}</div>
        ) : null
      }

      {/* Rollover expiry warning */}
      {rolloverExpiry?.expired && rolloverExpiry.expired > 0 ? (
        <div className="balance-card-warning">
          ⚠️{' '}
          {formatMessage(
            { id: hourlyAccounting ? 'balanceCard.rolloverExpiredHours' : 'balanceCard.rolloverExpiredDays' },
            {
              expiringDays: rolloverExpiry.expired,
              totalRolloverDays: rolloverExpiry.totalRollover,
              expiryDate: dayjs(rolloverExpiry.expiryDate).format('MMMM DD, YYYY'),
            }
          )}
        </div>
      ) : null}

      {rolloverExpiry?.willExpire && rolloverExpiry.willExpire > 0 ? (
        <div className="balance-card-warning">
          ⚠️{' '}
          {formatMessage(
            { id: hourlyAccounting ? 'balanceCard.rolloverExpiryHours' : 'balanceCard.rolloverExpiryDays' },
            {
              expiringDays: rolloverExpiry.willExpire,
              totalRolloverDays: rolloverExpiry.totalRollover,
              expiryDate: dayjs(rolloverExpiry.expiryDate).format('MMMM DD, YYYY'),
            }
          )}
        </div>
      ) : null}
    </Card>
  )
}

// Memoize component to prevent unnecessary re-renders
// Only re-render if critical props change
const BalanceCard = React.memo(BalanceCardComponent, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  return (
    prevProps.selectedLeaveType === nextProps.selectedLeaveType &&
    prevProps.rolloverExpiry === nextProps.rolloverExpiry &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.className === nextProps.className &&
    // Deep comparison for arrays - check if same reference or same content
    prevProps.currentYearLeaveTypes === nextProps.currentYearLeaveTypes &&
    prevProps.user === nextProps.user &&
    // Function comparison - assumes stable references
    prevProps.onLeaveTypeChange === nextProps.onLeaveTypeChange
  )
})

// Set display name for debugging
BalanceCard.displayName = 'BalanceCard'

export default BalanceCard
